import { memo, useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import BlockIcon from '@mui/icons-material/Block';
import { useStoreList } from '../../../../context/StoreListContext';
import { getStoreSearchLabel, storeMatchesSearch } from '../../../../utils/storeHelpers';
import useCashdeskTargetOptions, { CARD_TARGET_CATEGORY_ID } from '../../../../hooks/Marketing/useCashdeskTargetOptions';

export interface CashdeskTargetValue {
  targetCategoryId: number | null;
  identifierTypeId: number | null;
  entityIds: number[];
}

interface CashdeskTargetPickerProps {
  value: CashdeskTargetValue;
  onChange: (value: CashdeskTargetValue) => void;
}

function CashdeskTargetPicker({ value, onChange }: CashdeskTargetPickerProps) {
  const { categories, identifierTypes, loading, error } = useCashdeskTargetOptions(true);
  const { stores } = useStoreList();

  const storeOptions = useMemo(() => stores ?? [], [stores]);
  const selectedStores = useMemo(
    () => storeOptions.filter(s => value.entityIds.includes(Number(s.ENTITY_ID))),
    [storeOptions, value.entityIds]
  );

  const needsIdentifierType = value.targetCategoryId === CARD_TARGET_CATEGORY_ID;

  const setEntities = (ids: number[]) => onChange({ ...value, entityIds: ids });

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Ciblage propre au canal Caisse : aucune requête n'est nécessaire, choisissez la catégorie de clients
        et les magasins participants.
      </Typography>

      {error && (
        <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="cashdesk-target-category">Catégorie de clients *</InputLabel>
          <Select<number | ''>
            labelId="cashdesk-target-category"
            label="Catégorie de clients *"
            value={value.targetCategoryId ?? ''}
            onChange={e => {
              const id = e.target.value === '' ? null : e.target.value;
              onChange({
                ...value,
                targetCategoryId: id,
                identifierTypeId: id === CARD_TARGET_CATEGORY_ID ? value.identifierTypeId : null,
              });
            }}
            endAdornment={loading ? <CircularProgress size={16} sx={{ mr: 3 }} /> : undefined}
          >
            {categories.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {needsIdentifierType && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="cashdesk-target-identifier">Type de carte *</InputLabel>
            <Select<number | ''>
              labelId="cashdesk-target-identifier"
              label="Type de carte *"
              value={value.identifierTypeId ?? ''}
              onChange={e => onChange({ ...value, identifierTypeId: e.target.value === '' ? null : e.target.value })}
            >
              {identifierTypes.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
          {selectedStores.length} magasin(s) sélectionné(s)
        </Typography>
        <Autocomplete
          multiple
          size="small"
          disableCloseOnSelect
          options={storeOptions}
          value={selectedStores}
          getOptionLabel={getStoreSearchLabel}
          isOptionEqualToValue={(a, b) => a.ENTITY_ID === b.ENTITY_ID}
          filterOptions={(opts, state) => opts.filter(s => storeMatchesSearch(s, state.inputValue))}
          onChange={(_, selected) => setEntities(selected.map(s => Number(s.ENTITY_ID)))}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.ENTITY_ID}>
              <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
              {getStoreSearchLabel(option)}
            </li>
          )}
          renderInput={params => <TextField {...params} label="Magasins" placeholder="Rechercher un magasin" />}
        />
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<StarIcon />}
            sx={{ textTransform: 'none' }}
            onClick={() => setEntities(storeOptions.map(s => Number(s.ENTITY_ID)))}
          >
            Tous mes magasins
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<BlockIcon />}
            sx={{ textTransform: 'none' }}
            onClick={() => setEntities([])}
          >
            Vider ma sélection
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default memo(CashdeskTargetPicker);
